import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { INBOX_PLATFORMS } from "@content-os/config";
import { InboxFilters, type InboxStatusFilter } from "@/components/inbox/inbox-filters";
import { INBOX_WRITE_ROLES } from "@/lib/inbox-roles";
import { useBrandContext } from "@/contexts/brand-context";
import { useAccounts } from "@/hooks/use-accounts";
import { useInboxConversations } from "@/hooks/use-inbox-conversations";
import { useInboxConversation, useInboxOlderMessages } from "@/hooks/use-inbox-conversation";
import { ConversationList } from "@/components/inbox/conversation-list";
import { ConversationThread } from "@/components/inbox/conversation-thread";
import { Composer, type ComposerExtra } from "@/components/inbox/composer";
import { DraftCard } from "@/components/inbox/draft-card";
import { useArchiveConversation } from "@/hooks/use-inbox-conversation-actions";
import { usePermissions } from "@/hooks/use-permissions";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";


export function InboxDmsPage() {
  const { t } = useTranslation("inbox");
  const { brandSlug, convId } = useParams<{ brandSlug: string; convId?: string }>();
  const { brand } = useBrandContext();
  const navigate = useNavigate();
  const { role } = usePermissions();
  const archiveMut = useArchiveConversation(brand.id);
  const [statusFilter, setStatusFilter] = useState<InboxStatusFilter>("OPEN");
  const [channel, setChannel] = useState<string>(""); // "" = all channels

  function handleArchive(id: string) {
    archiveMut.mutate(
      { conversationId: id, status: "ARCHIVED" },
      {
        onSuccess: () => {
          // If the archived conversation is open, leave the thread view.
          if (id === convId) navigate(`/${brandSlug}/inbox/dms`);
          toast.success(t("archive.done"), {
            action: {
              label: t("archive.undo"),
              onClick: () =>
                archiveMut.mutate({ conversationId: id, status: "OPEN" }),
            },
          });
        },
        onError: () => toast.error(t("archive.error")),
      },
    );
  }

  const convsQuery = useInboxConversations({
    brandId: brand.id,
    status: statusFilter,
    platform: channel || undefined,
  });
  const { data: accounts } = useAccounts(brand.id);
  const hasActiveAccount = (accounts ?? []).some(
    (a) => a.status === "ACTIVE" && (INBOX_PLATFORMS as string[]).includes(a.platform),
  );
  const conversations = useMemo(
    () => convsQuery.data?.pages.flatMap((p) => p.conversations) ?? [],
    [convsQuery.data],
  );

  const convQuery = useInboxConversation(brand.id, convId ?? null);
  const firstPage = convQuery.data;
  const olderQuery = useInboxOlderMessages(
    brand.id,
    convId ?? null,
    firstPage?.nextCursor ?? null,
  );

  const allMessages = useMemo(() => {
    if (!firstPage) return [];
    const older = olderQuery.data?.pages.flatMap((p) => p.messages) ?? [];
    return [...firstPage.messages, ...older];
  }, [firstPage, olderQuery.data]);

  // AI suggested reply: rendered between the thread and the composer input
  // via the composer's extras slot; Edit pre-fills the composer textarea.
  const aiDraft = firstPage?.conversation.aiDraft ?? null;
  const composerExtras = useMemo<ComposerExtra[]>(() => {
    if (!aiDraft || aiDraft.status !== "PENDING") return [];
    return [
      {
        id: "ai-draft",
        render: ({ setDraft }) => (
          <DraftCard brandId={brand.id} draft={aiDraft} onEdit={setDraft} />
        ),
      },
    ];
  }, [aiDraft, brand.id]);

  return (
    <div className="flex h-full">
      {/* Conversation list */}
      <div className="w-80 flex-shrink-0 border-r border-border bg-surface-0 flex flex-col">
        <InboxFilters
          status={statusFilter}
          onStatusChange={setStatusFilter}
          channel={channel}
          onChannelChange={setChannel}
        />
        <div className="flex-1 overflow-y-auto">
        <ConversationList
          conversations={conversations}
          selectedId={convId ?? null}
          onSelect={(id) => navigate(`/${brandSlug}/inbox/dms/${id}`)}
          isLoading={convsQuery.isLoading}
          hasNextPage={convsQuery.hasNextPage}
          isFetchingNextPage={convsQuery.isFetchingNextPage}
          onLoadMore={() => convsQuery.fetchNextPage()}
          onArchive={
            INBOX_WRITE_ROLES.has(role) && statusFilter !== "ARCHIVED"
              ? handleArchive
              : undefined
          }
          emptyExtra={
            !hasActiveAccount ? (
              <Link
                to={`/${brandSlug}/settings/accounts`}
                className="mt-2 text-xs text-brand hover:underline"
              >
                {t("dmsPage.connectAccount")}
              </Link>
            ) : undefined
          }
        />
        </div>
      </div>

      {/* Thread + composer */}
      <div className="flex-1 flex flex-col min-w-0 bg-background">
        {!convId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-muted-foreground">
            <MessageCircle size={40} className="mb-3 opacity-50" />
            <p className="text-sm">{t("dmsPage.selectConversation")}</p>
          </div>
        ) : convQuery.isLoading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            {t("dmsPage.loadingConversation")}
          </div>
        ) : !firstPage ? (
          <div className="flex-1 flex items-center justify-center text-sm text-destructive">
            {t("dmsPage.failedConversation")}
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0">
              <ConversationThread
                conversation={firstPage.conversation}
                messages={allMessages}
                hasOlder={!!olderQuery.hasNextPage || !!firstPage.nextCursor && olderQuery.data === undefined}
                isFetchingOlder={olderQuery.isFetchingNextPage}
                onLoadOlder={() => olderQuery.fetchNextPage()}
              />
            </div>
            <Composer
              conversation={firstPage.conversation}
              messages={allMessages}
              extras={composerExtras}
            />
          </>
        )}
      </div>
    </div>
  );
}
